import { Component } from "react";
import { gql } from "@apollo/client";
import { graphql, withApollo } from "@apollo/client/react/hoc";
import { Icon } from "@hireart/component-library";
import checkCircleIcon from "hireart-icons/check-circle.svg";
import chevronRightIcon from "hireart-icons/chevron-right.svg";
import loadExternal from "load-external";
import { connect } from "react-redux";
import { Loader } from "semantic-ui-react";

const JAVASCRIPT_URL =
  "https://s3.amazonaws.com/cdn.hellosign.com/public/js/hellosign-embedded.LATEST.min.js";

const SignatureUrlQuery = gql`
  query HelloSignEmbeddedSignatureUrl($signature_id: String!) {
    hellosign_embedded_signature_url(signature_id: $signature_id)
  }
`;

export class HelloSignEmbedded extends Component {
  constructor(props) {
    super(props);
    this.state = {
      jsDidLoad: false,
      fetchingSignatureUrl: false,
      completedSignatureId: props.signature_id,
    };
  }

  componentDidMount() {
    loadExternal(JAVASCRIPT_URL, this.init.bind(this));
  }

  onMessage(eventData) {
    const { onComplete, data } = this.props;

    if (eventData.event !== HelloSign.EVENT_SIGNED) {
      return;
    }

    const signatureRequest = data.hellosign_embedded_signature_request;

    // One embedded signer is mounted per document, all at the same time. The
    // HelloSign global relays each signed event to every open listener, so
    // ignore events that don't belong to this signer's own signature —
    // otherwise signing one document fires onComplete for the others too and
    // marks them complete without ever being signed.
    if (
      signatureRequest?.signature_id &&
      eventData.signature_id !== signatureRequest.signature_id
    ) {
      return;
    }

    this.setState({ completedSignatureId: eventData.signature_id });
    if (onComplete) {
      onComplete(eventData.signature_id, signatureRequest.signature_request_id);
    }
  }

  canOpenForSignature() {
    const { data } = this.props;

    return (
      !data.loading &&
      this.state.jsDidLoad &&
      !this.state.completedSignatureId &&
      !this.state.fetchingSignatureUrl
    );
  }

  init() {
    const { client_id } = this.props.constants.hellosign;

    HelloSign.init(client_id);
    this.setState({ jsDidLoad: true });
  }

  open = () => {
    if (!this.canOpenForSignature()) {
      return null;
    }

    const {
      constants: { rails_env },
    } = this.props;

    this.setState({ fetchingSignatureUrl: true });

    this.props.client
      .query({
        query: SignatureUrlQuery,
        variables: {
          signature_id:
            this.props.data.hellosign_embedded_signature_request.signature_id,
        },
        fetchPolicy: "network-only", // can't reuse the signature url. and we need to call HelloSign.open every time.
      })
      .then((result) => {
        HelloSign.open({
          url: result.data.hellosign_embedded_signature_url,
          allowCancel: true,
          messageListener: this.onMessage.bind(this),
          skipDomainVerification: rails_env !== "production",
        });
      })
      .catch((err) => {
        console.log("catch", err);
      })
      .finally(() => {
        this.setState({ fetchingSignatureUrl: false });
      });
  };

  render() {
    let AsComponent = this.props.as;
    if (!AsComponent) {
      AsComponent = "a";
    }

    if (this.state.completedSignatureId) {
      return (
        <AsComponent>
          <Icon src={checkCircleIcon} className="text-green-60" />
        </AsComponent>
      );
    }

    if (this.state.fetchingSignatureUrl) {
      if (this.props.as) {
        return <AsComponent disabled isLoading />;
      }

      return <Loader active inline size="tiny" style={{ float: "right" }} />;
    }

    return (
      <AsComponent
        href="#"
        onClick={this.open}
        disabled={!this.canOpenForSignature()}
        className="inline-flex items-center gap-4 whitespace-nowrap"
      >
        Review & Sign
        <Icon src={chevronRightIcon} className="h-12 w-12" />
      </AsComponent>
    );
  }
}

const SignatureRequestQuery = gql`
  query HelloSignEmbeddedSignatureRequest(
    $email: String!
    $name: String!
    $template_id: String!
    $role: String!
    $onboardingToken: String
  ) {
    hellosign_embedded_signature_request(
      email: $email
      name: $name
      template_id: $template_id
      role: $role
      onboardingToken: $onboardingToken
    ) {
      signature_id
      signature_request_id
    }
  }
`;

const Query = graphql(SignatureRequestQuery);

const mapStateToProps = ({ constants }) => ({ constants });

export default connect(mapStateToProps)(withApollo(Query(HelloSignEmbedded)));
